import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Link as LinkIcon, ChevronDown, Check } from "lucide-react";
import { API } from "./constants";

/**
 * Self-service card for the currently-logged-in SuperAdmin to bind their
 * OWN account to a real tenant organization. Useful in two cases:
 *
 *  1. A founder/owner SA who wants their global admin account to ALSO act
 *     as a regular member of the tenant they own — so per-tenant queries
 *     match naturally without needing the SA bypass.
 *  2. After a deploy where prod's DB has a different mapping than preview.
 *
 * The root SA's row is locked from the general edit-org button (immutable
 * by the regular endpoint), so this self-only path is the supported way
 * for root to manage their own attachment.
 */
export function SelfOrganizationCard({ currentUser, organizations, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(currentUser?.organization_id || "");
  const [saving, setSaving] = useState(false);

  if (!currentUser || currentUser.role !== "superadmin") return null;

  const currentName = currentUser.organization_name || "(sin organización)";

  const handleSave = async () => {
    if (!picked) {
      toast.error("Selecciona una organización válida");
      return;
    }
    if (picked === currentUser.organization_id) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/me/organization`,
        { organization_id: picked },
        { withCredentials: true },
      );
      toast.success(
        `Tu cuenta ahora está vinculada a "${res.data.new_organization_name}"`,
      );
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al vincular organización");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="self-organization-card"
      className="bg-slate-900/50 border border-amber-500/20 rounded-xl p-4"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="p-2 bg-amber-500/10 rounded-lg shrink-0">
          <Building2 className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            Mi organización vinculada
            <span className="text-[10px] uppercase tracking-wider text-amber-400 px-1.5 py-0.5 bg-amber-500/10 rounded">
              Self-service
            </span>
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-[640px]">
            Vincula tu cuenta de SuperAdmin a una organización real. Mantienes acceso
            global al panel, pero tus queries de cliente, screening y documentos
            quedan naturalmente filtradas a esa org sin necesitar bypass.
          </p>
          <p
            data-testid="self-org-current"
            className="mt-2 text-sm text-slate-200 inline-flex items-center gap-2"
          >
            <LinkIcon className="w-3.5 h-3.5 text-teal-400" />
            Actualmente:{" "}
            <span className="font-medium text-teal-300">{currentName}</span>
          </p>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="self-org-toggle-btn"
          className="px-3 py-1.5 text-xs rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 inline-flex items-center gap-1.5 transition-colors"
        >
          {currentUser.organization_id ? "Cambiar" : "Vincular"}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div
          data-testid="self-org-picker"
          className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-2 flex-wrap"
        >
          <select
            value={picked || ""}
            onChange={(e) => setPicked(e.target.value)}
            data-testid="self-org-select"
            className="text-sm px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white max-w-[300px] flex-1 min-w-[200px]"
          >
            {!picked && (
              <option value="" disabled>
                — Selecciona una organización —
              </option>
            )}
            {organizations.map((org) => (
              <option key={org.organization_id} value={org.organization_id}>
                {org.name}
                {org.status === "suspended" ? " (suspendida)" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={handleSave}
            disabled={saving || !picked}
            data-testid="self-org-save-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md bg-amber-500 text-slate-950 font-medium hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check className="w-3.5 h-3.5" />
            {saving ? "Guardando..." : "Vincular"}
          </button>
          <button
            onClick={() => setOpen(false)}
            data-testid="self-org-cancel-btn"
            className="px-3 py-2 text-sm rounded-md text-slate-400 hover:bg-slate-800"
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}
